import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  ChevronDown,
  Loader2,
  Lock,
  MailCheck,
  ShieldCheck,
} from "lucide-react";
import { API } from "@/config/site";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMAIL_RE = /\S+@\S+\.\S+/;

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });

export default function Checkout() {
  const store = useStore();
  const { items, buyNow } = useCart();
  const { user } = useAuth();
  const [params] = useSearchParams();

  const [email, setEmail] = useState(user?.email || "");
  const [ack, setAck] = useState(false);
  const [paying, setPaying] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [activePromos, setActivePromos] = useState([]);
  const [activeEcodes, setActiveEcodes] = useState([]);

  useEffect(() => {
    if (user?.email) setEmail((e) => e || user.email);
  }, [user]);

  useEffect(() => {
    if (params.get("cancelled")) toast.info("Payment cancelled — your cart is untouched.");
  }, [params]);

  useEffect(() => {
    axios.get(`${API}/coupons/active`, { params: { market: store.market || "all", currency: store.currency || "USD" } }).then((r) => setActivePromos(Array.isArray(r.data) ? r.data : [])).catch(() => setActivePromos([]));
  }, [store.market, store.currency]);

  useEffect(() => {
    setAppliedCoupon(null);
    setCoupon("");
  }, [store.market, store.currency]);

  useEffect(() => {
    if (!user) { setActiveEcodes([]); return; }
    axios.get(`${API}/ecodes/available`, { params: { market: store.market || "all" } }).then((r) => setActiveEcodes(Array.isArray(r.data) ? r.data : [])).catch(() => setActiveEcodes([]));
  }, [user, store.market]);

  const rawItems = useMemo(() => (buyNow ? [buyNow] : items), [buyNow, items]);
  const rows = useMemo(
    () =>
      rawItems
        .map((item) => ({ item, product: store.productBySlug(item.product_id) }))
        .filter((r) => r.product),
    [rawItems, store.productsMap]
  );
  const source = buyNow ? "buynow" : "cart";

  const totalDisplay = rows.reduce(
    (s, r) => s + store.convert(Number(r.item.unit_price ?? r.item.denomination) * r.item.quantity, r.item.source_currency || r.product.currency),
    0
  );
  const needsAck = rows.some((r) => r.product.restrictions);
  const discountDisplay = appliedCoupon ? Number(appliedCoupon.discount || 0) : 0;
  const payableDisplay = Math.max(totalDisplay - discountDisplay, 0);
  const automaticPromotions = useMemo(() => {
    const now = Date.now();
    return (store.banners || [])
      .filter((b) => b.enabled && b.discount_type && b.discount_type !== "none")
      .filter((b) => !b.start_at || !Number.isFinite(Date.parse(b.start_at)) || Date.parse(b.start_at) <= now)
      .filter((b) => !b.end_at || !Number.isFinite(Date.parse(b.end_at)) || Date.parse(b.end_at) >= now)
      .filter((b) => !b.market || b.market === "all" || b.market === store.market)
      .filter((b) => !b.currency || b.currency === "all" || b.currency === store.currency)
       .filter((b) => b.discount_type !== "fixed" || (b.currency && b.currency !== "all"))
      .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0));
  }, [store.banners, store.market, store.currency]);

  const automaticDiscountDisplay = useMemo(() => {
    let discount = 0;
    for (const row of rows) {
      const promotion = automaticPromotions.find((p) => (!p.category_slug || p.category_slug === row.product.category) && (!p.target_products?.length || p.target_products.includes(row.product.slug)));
      if (!promotion) continue;
      const line = store.convert(Number(row.item.unit_price ?? row.item.denomination) * row.item.quantity, row.item.source_currency || row.product.currency);
      discount += promotion.discount_type === "fixed"
        ? Math.min(Number(promotion.discount_value || 0), line)
        : line * Number(promotion.discount_value || 0) / 100;
    }
    return Math.min(discount, totalDisplay);
  }, [rows, automaticPromotions, totalDisplay, store]);
  const payableWithPromotions = Math.max(totalDisplay - automaticDiscountDisplay - discountDisplay, 0);
  const outOfMarket = rows.filter(
    (r) => store.market && !store.isAvailableForMarket(r.product)
  );

  if (rows.length === 0) {
    return (
      <div className="vault-container flex flex-col items-center pt-28 text-center" data-testid="checkout-empty-state">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Nothing to check out yet</h1>
        <p className="mt-2 text-sm text-muted-foreground">Add a gift card first.</p>
        <Link
          to={store.market ? `/market/${store.market}` : "/cards"}
          data-testid="checkout-empty-browse"
          className="mt-8 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
        >
          Browse gift cards
        </Link>
      </div>
    );
  }

  const pay = async () => {
    if (!EMAIL_RE.test(email)) {
      toast.error("Enter a valid email — your code is delivered there.");
      return;
    }
    if (needsAck && !ack) {
      toast.error("Please confirm the region-compatibility checkbox before paying.");
      return;
    }
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: rows.map((r) => ({
          product_id: r.item.product_id,
          category: r.product.category,
          denomination: r.item.denomination,
          quantity: r.item.quantity,
        })),
        currency: store.currency,
        email,
        market: store.market || "all",
        origin_url: window.location.origin,
        restrictions_acknowledged: ack,
        coupon_code: appliedCoupon?.code || undefined,
      });
      localStorage.setItem("vault_pending_source", source);
      await loadRazorpayScript();
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Zeyinx",
        description: `Order ${data.order_id}`,
        order_id: data.razorpay_order_id,
        prefill: { email },
        theme: { color: "#00F0FF" },
        method:
          data.currency === "INR"
            ? { upi: true, card: true, netbanking: false, wallet: false, emi: false, paylater: false }
            : { upi: false, card: true, netbanking: false, wallet: false, emi: false, paylater: false },
        modal: {
          ondismiss: () => {
            setPaying(false);
            toast.info("Payment cancelled — your cart is untouched.");
          },
        },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              order_id: data.order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            window.location.href = `/order-confirmation?order_id=${data.order_id}`;
          } catch {
            toast.error(
              "Payment could not be verified — if money was debited, contact support with your order ID."
            );
            setPaying(false);
          }
        },
      });
      rzp.on("payment.failed", () => {
        setPaying(false);
        toast.error("Payment failed — please try again.");
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPaying(false);
    }
  };

  const summaryBody = (
    <div className="flex flex-col gap-5">
      {rows.map((r, idx) => (
        <div key={idx} className="flex gap-4" data-testid={`summary-item-${idx}`}>
          <img
            src={r.product.image}
            alt={`${r.product.name} artwork`}
            className="aspect-[1.586/1] w-24 shrink-0 rounded-lg border border-white/[0.08] object-cover"
          />
          <div className="flex flex-1 items-start justify-between gap-2 text-sm">
            <div>
              <p className="font-medium">{r.product.name}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {store.fmtRaw(Number(r.item.unit_price ?? r.item.denomination), r.item.source_currency || r.product.currency)} × {r.item.quantity}
              </p>
              <p className="mt-1 text-[10px] text-muted-foreground/80">
                {r.product.region === "global" ? "🌐" : store.countryFlag(r.product.country)}{" "}
                {(r.product.redeemable_names || []).join(", ")}
              </p>
            </div>
            <span className="font-mono text-sm">
              {store.fmt(Number(r.item.unit_price ?? r.item.denomination) * r.item.quantity, r.item.source_currency || r.product.currency)}
            </span>
          </div>
        </div>
      ))}
      <div className="rounded-xl border border-white/[0.08] bg-background/40 p-3">{activePromos.length > 0 && <div className="mb-3 rounded-lg border border-primary/20 bg-primary/[0.05] p-3"><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">Available promotions</p>{activePromos.slice(0,3).map((p) => <button key={p.code} type="button" onClick={async () => { setCoupon(p.code); try { const r = await axios.post(`${API}/coupons/validate`, { code: p.code, amount: totalDisplay, currency: store.currency, market: store.market || "all", items: rows.map((x) => ({ product_id: x.item.product_id, category: x.product.category })) }); setAppliedCoupon(r.data); toast.success(`Applied ${p.code}`); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} className="mt-2 block w-full text-left text-xs hover:text-primary"><span className="font-mono font-semibold">{p.code}</span><span className="ml-2">{p.label}</span>{p.audience === "new_users" && <span className="ml-2 text-[10px] text-muted-foreground">new users</span>}{p.category_slug && <span className="ml-2 text-[10px] text-muted-foreground">· {p.category_slug}</span>}</button>)}</div>}<div className="flex gap-2"><Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" className="h-9"/><button onClick={async()=>{try{const r=await axios.post(`${API}/coupons/validate`,{code:coupon,amount:totalDisplay,currency:store.currency,market:store.market||"all",items:rows.map((x)=>({product_id:x.item.product_id,category:x.product.category}))});setAppliedCoupon(r.data);toast.success("Coupon applied");}catch(e){setAppliedCoupon(null);toast.error(formatApiError(e.response?.data?.detail));}}} disabled={!coupon.trim()} className="rounded-lg border border-white/15 px-3 text-xs disabled:opacity-40">Apply</button></div>{appliedCoupon&&<p className="mt-2 text-[11px] text-primary">Applied {appliedCoupon.code} · {store.fmtRaw(appliedCoupon.discount,store.currency)} off</p>}</div><div className="flex flex-col gap-2.5 border-t border-white/[0.08] pt-4 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="font-mono" data-testid="checkout-subtotal">{store.fmtRaw(totalDisplay, store.currency)}</span>
        </div>
        {automaticDiscountDisplay > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Promotion discount</span><span className="font-mono text-primary">-{store.fmtRaw(automaticDiscountDisplay, store.currency)}</span></div>}{appliedCoupon && <div className="flex justify-between"><span className="text-muted-foreground">Coupon discount</span><span className="font-mono text-primary">-{store.fmtRaw(discountDisplay, store.currency)}</span></div>}
        <div className="flex justify-between">
          <span className="text-muted-foreground">Service fee</span>
          <span className="text-muted-foreground">{store.fmtRaw(0, store.currency)} · none</span>
        </div>
        <div className="flex justify-between text-base">
          <span>Total</span>
          <span className="font-mono font-semibold" data-testid="checkout-total">{store.fmtRaw(payableWithPromotions, store.currency)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-xl border border-primary/25 bg-primary/[0.06] px-3.5 py-2.5 text-xs text-primary">
        <MailCheck className="h-4 w-4 shrink-0" />
        <span data-testid="checkout-delivery-label">Digital delivery by email</span>
      </div>
    </div>
  );

  return (
    <div className="vault-container pt-14 sm:pt-16" data-testid="checkout-page">
      <p className="overline-label">Checkout</p>
      <h1 className="mt-2 font-display text-3xl font-semibold tracking-tighter sm:text-4xl">
        Almost there.
      </h1>

      <div className="mt-6 rounded-2xl border border-white/[0.08] bg-card lg:hidden">
        <button
          data-testid="mobile-summary-toggle"
          onClick={() => setSummaryOpen((o) => !o)}
          aria-expanded={summaryOpen}
          className="flex w-full items-center justify-between px-5 py-4 text-sm"
        >
          <span className="text-muted-foreground">Order summary</span>
          <span className="flex items-center gap-2 font-mono font-semibold">
            {store.fmtRaw(totalDisplay, store.currency)}
            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${summaryOpen ? "rotate-180" : ""}`} />
          </span>
        </button>
        {summaryOpen && <div className="border-t border-white/[0.08] px-5 py-5">{summaryBody}</div>}
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
        <div className="flex flex-col gap-8">
          <div
            className="rounded-2xl border border-primary/30 bg-primary/[0.05] p-6"
            data-testid="checkout-confirmation-panel"
          >
            <div className="flex items-start justify-between gap-4">
              <p className="overline-label !text-primary/80">Confirm your purchase</p>
              <button
                data-testid="checkout-change-market"
                onClick={store.openMarketModal}
                className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-muted-foreground transition-colors duration-200 hover:border-white/30 hover:text-foreground"
              >
                Change
              </button>
            </div>
            <div className="mt-4 flex flex-col gap-5">
              {rows.map((r, idx) => (
                <div key={idx} className="text-sm leading-relaxed" data-testid={`confirm-item-${idx}`}>
                  <p>
                    You are purchasing: <strong className="font-medium">{r.product.name}</strong>
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    Value:{" "}
                    <span className="font-mono text-foreground">
                      {store.fmtRaw(Number(r.item.unit_price ?? r.item.denomination), r.item.source_currency || r.product.currency)}
                    </span>
                    {r.item.quantity > 1 && <span> × {r.item.quantity}</span>} · Redeemable in:{" "}
                    <span className="text-foreground">{(r.product.redeemable_names || []).join(", ")}</span>{" "}
                    · Delivery: Digital code sent by email
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-5 border-t border-white/[0.08] pt-4 text-xs leading-relaxed text-muted-foreground" data-testid="checkout-delivery-notice">
              {store.delivery.checkout_notice} {store.delivery.checkout_estimate}
            </p>
          </div>

          {outOfMarket.length > 0 && (
            <div
              className="flex gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-5"
              data-testid="checkout-market-warning"
            >
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-400" />
              <p className="text-sm leading-relaxed text-amber-200/90">
                {outOfMarket.length} item(s) aren't part of your selected {store.marketObj?.name}{" "}
                marketplace. They're redeemable only in the countries listed on each item above.
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="checkout-email" className="overline-label">Delivery email</Label>
            <Input
              id="checkout-email"
              data-testid="checkout-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-3"
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Your gift-card code will be delivered to this address after successful payment.
            </p>
          </div>

          <div
            className="flex gap-3 rounded-2xl border border-white/[0.1] bg-card p-5"
            data-testid="payment-method-razorpay"
          >
            <ShieldCheck className="h-5 w-5 shrink-0 text-primary" />
            <div>
              <p className="text-sm font-medium">Secure payment by Razorpay</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {store.currency === "INR"
                  ? "Pay by UPI or credit/debit card in Razorpay's secure window."
                  : "Pay securely by credit/debit card in Razorpay's secure window."}{" "}
                Your payment details never touch our servers.
              </p>
            </div>
          </div>

          {needsAck && (
            <label
              className="flex cursor-pointer items-start gap-3 rounded-2xl border border-white/[0.1] bg-card p-4"
              data-testid="restrictions-ack-label"
            >
              <input
                type="checkbox"
                data-testid="restrictions-ack-checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#00F0FF]"
              />
              <span className="text-xs leading-relaxed text-muted-foreground">
                I confirm that this gift card is compatible with my account/store region.
              </span>
            </label>
          )}

          <div
            className="flex gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-4"
            data-testid="checkout-region-warning"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
            <p className="text-xs leading-relaxed text-amber-200/90">
              Region check: each code redeems only in the countries listed on it above. Make sure
              the account you'll redeem with matches one of those countries.
            </p>
          </div>

          <button
            data-testid="pay-securely-button"
            onClick={pay}
            disabled={paying || (needsAck && !ack)}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-4 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
          >
            {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Pay securely — {store.fmtRaw(payableWithPromotions, store.currency)}
          </button>
        </div>

        <div className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-white/[0.08] bg-card p-6" data-testid="checkout-summary-sticky">
            <p className="overline-label mb-5">Order summary</p>
            {summaryBody}
          </div>
        </div>
      </div>
    </div>
  );
}
